import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import axios from "axios";
import { toast } from "sonner";

const LOGO_URL = "/images/singleseen_logo_transparent.png";

const SettingsPage = () => {
  const { t } = useTranslation();
  const { user, token, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingPassport, setUploadingPassport] = useState(false);
  const [options, setOptions] = useState(null);
  const [photoSettings, setPhotoSettings] = useState({ photo_visibility: "subscribers_only" });
  const [incomingRequests, setIncomingRequests] = useState([]);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    about_me: "",
    profession: "",
    city: "",
    country: "",
    education: "",
    looking_for: ""
  });

  const tabs = [
    { id: "profile", label: t('settings.profile'), icon: "01" },
    { id: "photos", label: t('settings.photos'), icon: "02" },
    { id: "subscription", label: t('settings.subscription'), icon: "03" },
    { id: "verification", label: t('settings.verification'), icon: "04" },
    { id: "account", label: t('settings.account'), icon: "05" }
  ];

  useEffect(() => {
    fetchOptions();
    if (user) {
      setFormData({
        about_me: user.about_me || "",
        profession: user.profession || "",
        city: user.city || "",
        country: user.country || "",
        education: user.education || "",
        looking_for: user.looking_for || ""
      });
      fetchPhotoSettings();
      fetchIncomingRequests();
      fetchVerificationStatus();
    }
  }, [user]);

  const fetchOptions = async () => {
    try {
      const response = await axios.get(`${API}/config/options`);
      setOptions(response.data);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  };

  const fetchPhotoSettings = async () => {
    try {
      const response = await axios.get(`${API}/photos/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotoSettings(response.data);
    } catch (error) {
      console.error("Error fetching photo settings:", error);
    }
  };

  const fetchIncomingRequests = async () => {
    try {
      const response = await axios.get(`${API}/photos/requests/incoming`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIncomingRequests(response.data.filter(r => r.status === 'pending'));
    } catch (error) {
      console.error("Error fetching requests:", error);
    }
  };

  const fetchVerificationStatus = async () => {
    try {
      const response = await axios.get(`${API}/verification/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerificationStatus(response.data);
    } catch (error) {
      console.error("Error fetching verification status:", error);
    }
  };

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('settings.profileUpdated'));
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error updating profile");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      await axios.post(`${API}/profile/photos`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success(t('settings.photoUploaded'));
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error uploading photo");
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoUrl) => {
    try {
      await axios.delete(`${API}/profile/photos`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { photo_url: photoUrl }
      });
      toast.success(t('settings.photoDeleted'));
      refreshUser();
    } catch (error) {
      toast.error("Error deleting photo");
    }
  };

  const handlePhotoSettingsUpdate = async (visibility) => {
    try {
      await axios.put(`${API}/photos/settings`, 
        { photo_visibility: visibility },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPhotoSettings({ ...photoSettings, photo_visibility: visibility });
      toast.success(t('settings.privacyUpdated'));
    } catch (error) {
      toast.error("Error updating privacy settings");
    }
  };

  const handlePhotoAccessResponse = async (requestId, approve) => {
    try {
      await axios.post(`${API}/photos/requests/${requestId}/respond`,
        { approve },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(approve ? t('settings.accessApproved') : t('settings.accessDenied'));
      fetchIncomingRequests();
    } catch (error) {
      toast.error("Error responding to request");
    }
  };

  const handlePassportUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploadingPassport(true);
    try {
      await axios.post(`${API}/profile/verify-id`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success(t('settings.idSubmitted'));
      fetchVerificationStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error uploading ID");
    } finally {
      setUploadingPassport(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm(t('settings.deleteAccountConfirm'))) return;
    if (!window.confirm(t('settings.deleteAccountFinal'))) return;

    try {
      await axios.delete(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('settings.accountDeleted'));
      logout();
      navigate("/");
    } catch (error) {
      toast.error("Error deleting account");
    }
  };

  const subscriptionInfo = user?.subscription_info || {};
  const hasActiveSubscription = user?.subscription && user.subscription !== 'free';

  return (
    <div className="min-h-screen bg-rose-50">
      {/* Header */}
      <nav className="bg-white/90 backdrop-blur-md border-b border-rose-100 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="SingleSeen" className="h-12 sm:h-16 w-auto" />
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/dashboard" className="text-rose-600 hover:text-rose-800 text-sm sm:text-base">
                ← Dashboard
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        <h1 className="font-serif text-2xl sm:text-3xl text-rose-900 mb-6">{t('settings.title')}</h1>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Tab Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            {/* Mobile Tab Selector */}
            <div className="lg:hidden mb-4">
              <Select value={activeTab} onValueChange={setActiveTab}>
                <SelectTrigger className="w-full bg-white border-rose-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tabs.map(tab => (
                    <SelectItem key={tab.id} value={tab.id}>
                      <span className="text-rose-400 mr-2">{tab.icon}</span> {tab.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Desktop Tab Navigation */}
            <div className="hidden lg:block bg-white rounded-xl border border-rose-100 p-2">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === tab.id
                      ? "bg-rose-100 text-rose-900 font-medium"
                      : "text-rose-600 hover:bg-rose-50"
                  }`}
                >
                  <span className="text-lg font-light text-rose-400">{tab.icon}</span>
                  <span className="text-sm">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1">
            {/* Profile Tab */}
            {activeTab === "profile" && (
              <div className="bg-white rounded-xl sm:rounded-2xl border border-rose-100 p-4 sm:p-6">
                <h2 className="font-serif text-xl text-rose-900 mb-6">{t('settings.profileInfo')}</h2>
                
                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <Label className="text-rose-700">{t('settings.aboutMe')}</Label>
                    <Textarea
                      value={formData.about_me}
                      onChange={(e) => setFormData({...formData, about_me: e.target.value})}
                      placeholder={t('settings.aboutMePlaceholder')}
                      className="mt-2 bg-rose-50 border-rose-200 min-h-[120px]"
                      maxLength={500}
                    />
                    <p className="text-rose-400 text-xs mt-1">{formData.about_me.length}/500</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-rose-700">{t('settings.profession')}</Label>
                      <Input
                        value={formData.profession}
                        onChange={(e) => setFormData({...formData, profession: e.target.value})}
                        placeholder={t('settings.professionPlaceholder')}
                        className="mt-2 bg-rose-50 border-rose-200"
                      />
                    </div>
                    <div>
                      <Label className="text-rose-700">{t('settings.education')}</Label>
                      <Select
                        value={formData.education}
                        onValueChange={(v) => setFormData({...formData, education: v})}
                      >
                        <SelectTrigger className="mt-2 bg-rose-50 border-rose-200">
                          <SelectValue placeholder={t('settings.selectEducation')} />
                        </SelectTrigger>
                        <SelectContent>
                          {options?.education_levels?.map(level => (
                            <SelectItem key={level} value={level}>{level}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-rose-700">{t('settings.city')}</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        placeholder={t('settings.cityPlaceholder')}
                        className="mt-2 bg-rose-50 border-rose-200"
                      />
                    </div>
                    <div>
                      <Label className="text-rose-700">{t('settings.country')}</Label>
                      <Select
                        value={formData.country}
                        onValueChange={(v) => setFormData({...formData, country: v})}
                      >
                        <SelectTrigger className="mt-2 bg-rose-50 border-rose-200">
                          <SelectValue placeholder={t('settings.selectCountry')} />
                        </SelectTrigger>
                        <SelectContent>
                          {options?.countries?.map(country => (
                            <SelectItem key={country} value={country}>{country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-rose-700">{t('settings.lookingFor')}</Label>
                    <Textarea
                      value={formData.looking_for}
                      onChange={(e) => setFormData({...formData, looking_for: e.target.value})}
                      placeholder={t('settings.lookingForPlaceholder')}
                      className="mt-2 bg-rose-50 border-rose-200"
                      maxLength={300}
                    />
                  </div>

                  <Button
                    onClick={handleUpdateProfile}
                    disabled={loading}
                    className="w-full sm:w-auto bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full px-8"
                  >
                    {loading ? t('common.saving') : t('common.saveChanges')}
                  </Button>
                </div>
              </div>
            )}

            {/* Photos Tab */}
            {activeTab === "photos" && (
              <div className="space-y-6">
                {/* Current Photos */}
                <div className="bg-white rounded-xl sm:rounded-2xl border border-rose-100 p-4 sm:p-6">
                  <h2 className="font-serif text-xl text-rose-900 mb-4">{t('settings.yourPhotos')}</h2>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
                    {user?.photos?.map((photo, index) => (
                      <div key={index} className="relative aspect-square rounded-xl overflow-hidden bg-rose-100">
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${photo}`}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={() => handleDeletePhoto(photo)}
                          className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-lg hover:bg-red-600"
                          title={t('settings.deletePhoto')}
                        >
                          ×
                        </button>
                        {index === 0 && (
                          <span className="absolute bottom-2 left-2 bg-rose-500 text-white text-xs px-2 py-1 rounded-full">
                            Main
                          </span>
                        )}
                      </div>
                    ))}

                    {/* Upload Button */}
                    {(user?.photos?.length || 0) < 6 && (
                      <label className="aspect-square rounded-xl border-2 border-dashed border-rose-300 bg-rose-50 flex flex-col items-center justify-center cursor-pointer hover:border-rose-500 hover:bg-rose-100 transition-colors">
                        <span className="text-3xl text-rose-400 mb-2">+</span>
                        <span className="text-rose-500 text-xs sm:text-sm text-center px-2">
                          {uploading ? t('common.uploading') : t('settings.addPhoto')}
                        </span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          disabled={uploading}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Photo Privacy */}
                <div className="bg-white rounded-xl sm:rounded-2xl border border-rose-100 p-4 sm:p-6">
                  <h2 className="font-serif text-xl text-rose-900 mb-4">{t('settings.photoPrivacy')}</h2>
                  <p className="text-rose-600 text-sm mb-4">{t('settings.photoPrivacyDesc')}</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { value: "everyone", label: t('settings.visibilityEveryone'), desc: t('settings.visibilityEveryoneDesc') },
                      { value: "subscribers_only", label: t('settings.visibilitySubscribers'), desc: t('settings.visibilitySubscribersDesc') },
                      { value: "on_request", label: t('settings.visibilityOnRequest'), desc: t('settings.visibilityOnRequestDesc') },
                      { value: "hidden", label: t('settings.visibilityHidden'), desc: t('settings.visibilityHiddenDesc') }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => handlePhotoSettingsUpdate(option.value)}
                        className={`p-4 rounded-xl border-2 text-left transition-all ${
                          photoSettings.photo_visibility === option.value
                            ? "border-rose-500 bg-rose-50"
                            : "border-rose-200 hover:border-rose-300"
                        }`}
                      >
                        <div className="font-medium text-rose-900">{option.label}</div>
                        <div className="text-rose-500 text-xs mt-1">{option.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Photo Access Requests */}
                {incomingRequests.length > 0 && (
                  <div className="bg-white rounded-xl sm:rounded-2xl border border-rose-100 p-4 sm:p-6">
                    <h2 className="font-serif text-xl text-rose-900 mb-4">{t('settings.photoRequests')}</h2>
                    <div className="space-y-3">
                      {incomingRequests.map(request => (
                        <div key={request.id} className="flex items-center justify-between p-3 bg-rose-50 rounded-xl">
                          <div>
                            <p className="font-medium text-rose-900">{request.requester_name}</p>
                            <p className="text-rose-500 text-sm">{t('settings.wantsToSeePhotos')}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handlePhotoAccessResponse(request.id, true)}
                              className="bg-emerald-500 text-white hover:bg-emerald-600 rounded-full"
                            >
                              ✓
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handlePhotoAccessResponse(request.id, false)}
                              className="border-rose-300 text-rose-700 rounded-full"
                            >
                              ×
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Subscription Tab */}
            {activeTab === "subscription" && (
              <div className="space-y-6">
                {/* Current Plan */}
                <div className="bg-white rounded-xl sm:rounded-2xl border border-rose-100 p-4 sm:p-6">
                  <h2 className="font-serif text-xl text-rose-900 mb-4">{t('settings.currentPlan')}</h2>
                  
                  <div className={`p-4 sm:p-6 rounded-xl ${hasActiveSubscription ? 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200' : 'bg-rose-50 border border-rose-200'}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">{hasActiveSubscription ? '👑' : '🆓'}</span>
                          <h3 className="font-serif text-xl text-rose-900 capitalize">
                            {user?.subscription || 'Free'} Plan
                          </h3>
                        </div>
                        {hasActiveSubscription ? (
                          <div className="text-rose-600 text-sm space-y-1">
                            {subscriptionInfo.expires_at && (
                              <p>{t('settings.expiresOn')}: {new Date(subscriptionInfo.expires_at).toLocaleDateString()}</p>
                            )}
                            {subscriptionInfo.is_recurring && (
                              <p className="text-emerald-600">✓ {t('settings.autoRenewal')}</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-rose-600 text-sm">{t('settings.freePlanLimits')}</p>
                        )}
                      </div>
                      
                      {!hasActiveSubscription && (
                        <Button
                          onClick={() => navigate("/pricing")}
                          className="bg-gradient-to-r from-rose-500 to-pink-500 text-white hover:from-rose-600 hover:to-pink-600 rounded-full px-6"
                        >
                          {t('settings.upgradePlan')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Plan Benefits */}
                <div className="bg-white rounded-xl sm:rounded-2xl border border-rose-100 p-4 sm:p-6">
                  <h2 className="font-serif text-xl text-rose-900 mb-4">{t('settings.planBenefits')}</h2>
                  
                  <div className="space-y-3">
                    {[
                      { feature: t('settings.benefitViewPhotos'), free: "Blurred", basic: "✓", premium: "✓" },
                      { feature: t('settings.benefitSendInterests'), free: "3/day", basic: "15/day", premium: "Unlimited" },
                      { feature: t('settings.benefitChat'), free: "—", basic: "✓", premium: "✓" },
                      { feature: t('settings.benefitPrioritySupport'), free: "—", basic: "—", premium: "✓" },
                    ].map((row, i) => (
                      <div key={i} className="grid grid-cols-4 gap-2 py-2 border-b border-rose-100 last:border-0">
                        <div className="text-rose-700 text-sm col-span-1">{row.feature}</div>
                        <div className="text-center text-rose-500 text-sm">{row.free}</div>
                        <div className="text-center text-rose-700 text-sm font-medium">{row.basic}</div>
                        <div className="text-center text-amber-600 text-sm font-medium">{row.premium}</div>
                      </div>
                    ))}
                    <div className="grid grid-cols-4 gap-2 pt-2 text-xs text-rose-400">
                      <div></div>
                      <div className="text-center">Free</div>
                      <div className="text-center">Basic</div>
                      <div className="text-center">Premium</div>
                    </div>
                  </div>

                  <div className="mt-6 text-center">
                    <Button
                      onClick={() => navigate("/pricing")}
                      variant="outline"
                      className="border-rose-300 text-rose-700 hover:bg-rose-50 rounded-full"
                    >
                      {t('settings.comparePlans')}
                    </Button>
                  </div>
                </div>

                {/* Manage Subscription */}
                {hasActiveSubscription && (
                  <div className="bg-white rounded-xl sm:rounded-2xl border border-rose-100 p-4 sm:p-6">
                    <h2 className="font-serif text-xl text-rose-900 mb-4">{t('settings.manageSubscription')}</h2>
                    <div className="space-y-3">
                      <Button
                        variant="outline"
                        className="w-full sm:w-auto border-rose-300 text-rose-700 hover:bg-rose-50 rounded-full"
                        onClick={() => navigate("/pricing")}
                      >
                        {t('settings.changePlan')}
                      </Button>
                      <p className="text-rose-500 text-sm">
                        {t('settings.cancelNote')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Verification Tab */}
            {activeTab === "verification" && (
              <div className="bg-white rounded-xl sm:rounded-2xl border border-rose-100 p-4 sm:p-6">
                <h2 className="font-serif text-xl text-rose-900 mb-2">{t('settings.idVerification')}</h2>
                <p className="text-rose-600 text-sm mb-6">{t('settings.idVerificationDesc')}</p>

                {user?.is_id_verified ? (
                  <div className="p-6 bg-emerald-50 rounded-xl border border-emerald-200 text-center">
                    <span className="text-4xl block mb-3">✓</span>
                    <h3 className="font-serif text-xl text-emerald-800 mb-2">{t('settings.verified')}</h3>
                    <p className="text-emerald-600 text-sm">{t('settings.verifiedDesc')}</p>
                  </div>
                ) : verificationStatus?.status === 'pending' ? (
                  <div className="p-6 bg-amber-50 rounded-xl border border-amber-200 text-center">
                    <span className="text-4xl block mb-3">⏳</span>
                    <h3 className="font-serif text-xl text-amber-800 mb-2">{t('settings.verificationPending')}</h3>
                    <p className="text-amber-600 text-sm">{t('settings.verificationPendingDesc')}</p>
                  </div>
                ) : verificationStatus?.status === 'rejected' ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                      <h3 className="font-medium text-red-800 mb-1">{t('settings.verificationRejected')}</h3>
                      <p className="text-red-600 text-sm">{verificationStatus.rejection_reason || t('settings.tryAgain')}</p>
                    </div>
                    <label className="block">
                      <span className="text-rose-700 text-sm font-medium">{t('settings.uploadNewId')}</span>
                      <div className="mt-2 p-6 border-2 border-dashed border-rose-300 rounded-xl text-center cursor-pointer hover:border-rose-500 hover:bg-rose-50">
                        <span className="text-3xl block mb-2">📄</span>
                        <span className="text-rose-600 text-sm">{uploadingPassport ? t('common.uploading') : t('settings.clickToUpload')}</span>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handlePassportUpload}
                          disabled={uploadingPassport}
                          className="hidden"
                        />
                      </div>
                    </label>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-rose-50 rounded-xl">
                      <h3 className="font-medium text-rose-900 mb-2">{t('settings.whyVerify')}</h3>
                      <ul className="text-rose-600 text-sm space-y-2">
                        <li>→ {t('settings.verifyBenefit1')}</li>
                        <li>→ {t('settings.verifyBenefit2')}</li>
                        <li>→ {t('settings.verifyBenefit3')}</li>
                      </ul>
                    </div>
                    <label className="block">
                      <span className="text-rose-700 text-sm font-medium">{t('settings.uploadId')}</span>
                      <div className="mt-2 p-6 border-2 border-dashed border-rose-300 rounded-xl text-center cursor-pointer hover:border-rose-500 hover:bg-rose-50">
                        <span className="text-3xl block mb-2">📄</span>
                        <span className="text-rose-600 text-sm">{uploadingPassport ? t('common.uploading') : t('settings.clickToUpload')}</span>
                        <p className="text-rose-400 text-xs mt-2">{t('settings.acceptedFormats')}</p>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={handlePassportUpload}
                          disabled={uploadingPassport}
                          className="hidden"
                        />
                      </div>
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* Account Tab */}
            {activeTab === "account" && (
              <div className="space-y-6">
                {/* Account Info */}
                <div className="bg-white rounded-xl sm:rounded-2xl border border-rose-100 p-4 sm:p-6">
                  <h2 className="font-serif text-xl text-rose-900 mb-4">{t('settings.accountInfo')}</h2>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b border-rose-100">
                      <span className="text-rose-600">{t('common.email')}</span>
                      <span className="text-rose-900 font-medium">{user?.email}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-rose-100">
                      <span className="text-rose-600">{t('common.memberSince')}</span>
                      <span className="text-rose-900">{new Date(user?.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between items-center py-3">
                      <span className="text-rose-600">{t('common.emailVerified')}</span>
                      <span className={user?.email_verified ? "text-emerald-600" : "text-amber-600"}>
                        {user?.email_verified ? "✓ Verified" : "Not verified"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Logout */}
                <div className="bg-white rounded-xl sm:rounded-2xl border border-rose-100 p-4 sm:p-6">
                  <h2 className="font-serif text-xl text-rose-900 mb-4">{t('settings.session')}</h2>
                  <Button
                    onClick={() => { logout(); navigate("/"); }}
                    variant="outline"
                    className="w-full sm:w-auto border-rose-300 text-rose-700 hover:bg-rose-50 rounded-full"
                  >
                    {t('nav.logout')}
                  </Button>
                </div>

                {/* Danger Zone */}
                <div className="bg-white rounded-xl sm:rounded-2xl border border-red-200 p-4 sm:p-6">
                  <h2 className="font-serif text-xl text-red-800 mb-2">{t('settings.dangerZone')}</h2>
                  <p className="text-red-600 text-sm mb-4">{t('settings.deleteWarning')}</p>
                  <Button
                    onClick={handleDeleteAccount}
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50 rounded-full"
                  >
                    {t('settings.deleteAccount')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
